import moment from "moment-timezone";
import React, { useEffect, useState } from "react";
import { Form, Spinner } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";

import { useAppSelector } from "../../app/hook";
import {
  useUpdateAiWidgetAppearanceMutation,
  useUpdateMcBotWidgetAppearanceMutation
} from "../../app/services/chatbot-rtk.services";
import { confirm } from "../../components/common/confirmation/confirmation";
import OperatingHours, {
  convertH2M,
  DayRangesType,
  getTimeFromMins,
  splitArrayIntoChunksOfLen,
  weekDays
} from "../../components/dashboard/setting/operatinHourSlider/OperatingHours";
import LouiseButton from "../../components/ui/LouiseButton";
import ProBadge from "../../components/ui/ProBadge";
import usePermission from "../../hooks/usePermission";
import { IOperatingHours } from "../../interfaces/models/widget-customize.interface";
import { BotTypeEnum } from "../../utils/BotTypeEnum";
import { selectAppearanceState } from "../appearance/appearanceSlice";
import {
  AppearanceType,
  IAiAppearance,
  IOperatingTime
} from "../appearance/types";
import TimeZoneSelector from "./TimeZoneSelector";

const VisibilityManagerEdit = () => {
  const { t } = useTranslation();

  const permissions = usePermission();
  const appearanceState = useAppSelector(selectAppearanceState);

  const [updateAiWidgetAppearance, UpdateAiWidgetAppearanceResults] =
    useUpdateAiWidgetAppearanceMutation();
  const [updateMcBotWidgetAppearance, updateMcBotWidgetAppearanceResults] =
    useUpdateMcBotWidgetAppearanceMutation();

  const [formIsActive, setFormIsActive] =
    useState<IOperatingHours["isActive"]>(false);
  const [selectedFormTimezone, setSelectedFormTimezone] = useState<
    IOperatingHours["selectedTimezone"]
  >(
    (appearanceState?.configs as IAiAppearance)?.operatingHours
      ?.selectedTimezone || "UTC"
  );
  const [formTimes, setFormTimes] = useState<DayRangesType>({
    sunday: [],
    monday: [],
    tuesday: [],
    wednesday: [],
    thursday: [],
    friday: [],
    saturday: []
  });

  const adjustTimeRanges = (timeRanges: any, offset: any) => {
    const sortedData: {
      sunday: number[];
      monday: number[];
      tuesday: number[];
      wednesday: number[];
      thursday: number[];
      friday: number[];
      saturday: number[];
    } = {
      sunday: [],
      monday: [],
      tuesday: [],
      wednesday: [],
      thursday: [],
      friday: [],
      saturday: []
    };

    // const MINUTES_IN_DAY = 1440;
    const MINUTES_IN_DAY = 1439;

    const adjustedRanges: any = {};

    for (const [day, ranges] of Object.entries(timeRanges)) {
      if ((ranges as number[]).length > 0) {
        const newRanges = [];

        for (let i = 0; i < (ranges as number[]).length; i += 2) {
          const start = (ranges as number[])[i] - offset;
          const end = (ranges as number[])[i + 1] - offset;

          // Adjust times that go into the previous or next day
          if (start < 0) {
            newRanges.push(0);
            newRanges.push(end);
            // Add to previous day
            const prevDay = getPreviousDay(day);
            if (!adjustedRanges[prevDay]) {
              adjustedRanges[prevDay] = [];
            }
            adjustedRanges[prevDay].push(MINUTES_IN_DAY + start);
            adjustedRanges[prevDay].push(MINUTES_IN_DAY);
          } else if (end > MINUTES_IN_DAY) {
            newRanges.push(start);
            newRanges.push(MINUTES_IN_DAY);
            // Add to next day
            const nextDay = getNextDay(day);
            if (!adjustedRanges[nextDay]) {
              adjustedRanges[nextDay] = [];
            }
            adjustedRanges[nextDay].push(0);
            adjustedRanges[nextDay].push(end - MINUTES_IN_DAY);
          } else {
            newRanges.push(start);
            newRanges.push(end);
          }
        }

        adjustedRanges[day] = newRanges;
      }
    }

    for (const key in sortedData) {
      if (Object.prototype.hasOwnProperty.call(sortedData, key)) {
        for (const key2 in adjustedRanges) {
          if (Object.prototype.hasOwnProperty.call(adjustedRanges, key2)) {
            const element2 = adjustedRanges[key2];

            if (key === key2) {
              sortedData[key] = element2;
            }
          }
        }
      }
    }

    return sortedData;
  };

  const getPreviousDay = (day: string) => {
    const days: string[] = [
      "sunday",
      "monday",
      "tuesday",
      "wednesday",
      "thursday",
      "friday",
      "saturday"
    ];
    const index = days.indexOf(day.toLowerCase());
    return days[(index + 6) % 7];
  };

  const getNextDay = (day: string) => {
    const days: string[] = [
      "sunday",
      "monday",
      "tuesday",
      "wednesday",
      "thursday",
      "friday",
      "saturday"
    ];
    const index = days.indexOf(day.toLowerCase());
    return days[(index + 1) % 7];
  };

  const normalizedTimes = (times: IOperatingHours["times"] | undefined) => {
    const newTimes = {
      sunday: [],
      monday: [],
      tuesday: [],
      wednesday: [],
      thursday: [],
      friday: [],
      saturday: []
    } as { [key: string]: number[] };

    if (times) {
      for (const [key, value] of Object.entries(times)) {
        for (const keyNew in newTimes) {
          if (Object.prototype.hasOwnProperty.call(newTimes, keyNew)) {
            if (key === keyNew.substring(0, 3)) {
              value.forEach((time) => {
                newTimes[keyNew].push(
                  convertH2M(moment(time.from).utc().format("HH:mm"))
                );

                newTimes[keyNew].push(
                  convertH2M(moment(time.to).utc().format("HH:mm"))
                );
              });
            }
          }
        }
      }
    }

    return newTimes;
  };

  const denormalizedTimes = (times: DayRangesType) => {
    const newTimes = {
      sun: [],
      mon: [],
      tue: [],
      wed: [],
      thu: [],
      fri: [],
      sat: []
    } as { [key: string]: { from: string; to: string }[] };

    for (const day in times) {
      if (Object.prototype.hasOwnProperty.call(times, day)) {
        if (times[day as weekDays].length > 0) {
          const dayKey = day.slice(0, 3).toLowerCase(); // Abbreviate day names
          const ranges = times[day as weekDays];

          const chunks = splitArrayIntoChunksOfLen(ranges, 2);

          chunks.forEach((double) => {
            newTimes[dayKey].push({
              from: getTimeFromMins(
                double[0] + -moment().tz(selectedFormTimezone).utcOffset(),
                day as weekDays
              ).toISOString(),
              to: getTimeFromMins(
                double[1] + -moment().tz(selectedFormTimezone).utcOffset(),
                day as weekDays
              ).toISOString()
            });
          });
        } else {
          newTimes[day.slice(0, 3).toLowerCase()] = [];
        }
      }
    }

    return newTimes;
  };

  const handleSetVisibility = () => {
    const clonedData: AppearanceType =
      appearanceState.configs as AppearanceType;

    if (clonedData) {
      const formData = new FormData();

      if (
        clonedData &&
        (clonedData as IAiAppearance).logo &&
        (clonedData as IAiAppearance).logo !==
          "https://cdn.1cdn.app/application/LOUISEBOT/24050807084353_Logo_(2).png" &&
        (clonedData as IAiAppearance).logo !==
          "https://cdn.1cdn.app/application/LOUISEBOT/2312171433313_Ellipse_980.png" &&
        typeof (clonedData as IAiAppearance).logo === "object"
      ) {
        formData.append("avatar", (clonedData as IAiAppearance).logo);
      }

      if (
        clonedData &&
        (clonedData as any).icon &&
        typeof (clonedData as any).icon.value === "object" &&
        ((clonedData as IAiAppearance).icon.type === "image" ||
          (clonedData as IAiAppearance).icon.type === "video")
      ) {
        formData.append("icon", (clonedData as IAiAppearance).icon.value);
      }

      formData.append(
        "settings",
        JSON.stringify({
          ...{
            ...clonedData,
            name: clonedData?.name || "Louise",
            operatingHours: {
              isActive: formIsActive,
              times: {
                ...(denormalizedTimes(formTimes) as IOperatingTime["times"]),
                exc: []
              },
              selectedTimezone: selectedFormTimezone
            }
          },
          icon:
            appearanceState?.bot_code !== BotTypeEnum.HUMAN_CLONE
              ? (clonedData as IAiAppearance).icon
              : undefined,
          logo:
            (clonedData as IAiAppearance)?.logo !==
              "https://cdn.1cdn.app/application/LOUISEBOT/24050807084353_Logo_(2).png" &&
            (clonedData as IAiAppearance)?.logo !==
              "https://cdn.1cdn.app/application/LOUISEBOT/2312171433313_Ellipse_980.png" &&
            typeof (clonedData as IAiAppearance)?.logo === "object"
              ? ""
              : (clonedData as IAiAppearance)?.logo
        })
      );

      if (
        appearanceState.bot_code !== BotTypeEnum.TEXT_SCENARIO &&
        appearanceState.bot_code !== BotTypeEnum.VIDEO_SCENARIO
      )
        updateAiWidgetAppearance({ id: appearanceState.id, data: formData })
          .unwrap()
          .then(() => {
            toast.success("Successfully updated");

            //handleCancelAppearanceConfig();
          })
          .catch((err) => {
            toast.error(err.data.message || err.message);
          });
      else
        updateMcBotWidgetAppearance({
          id: appearanceState.id,
          data: formData
        })
          .unwrap()
          .then(() => {
            toast.success("Successfully updated");

            //handleCancelAppearanceConfig();
          })
          .catch((err) => {
            toast.error(err.data.message || err.message);
          });
    }
  };

  const handleTimeZoneChanged = (timezone: string) => {
    if (timezone !== selectedFormTimezone) {
      confirm(t("VisibilityManagerEdit/key9")).then((res) => {
        if (res) {
          handleReset();
          setSelectedFormTimezone(timezone);
        }
      });
    } else setSelectedFormTimezone(timezone);
  };

  const handleReset = () => {
    setSelectedFormTimezone("UTC");
    setFormTimes({
      friday: [],
      monday: [],
      saturday: [],
      sunday: [],
      thursday: [],
      tuesday: [],
      wednesday: []
    });
  };

  const handleUpdateTimes = (times: DayRangesType) => {
    setFormTimes({ ...formTimes, ...times });
  };

  // Preventing api calls when nothing is changed => (Mocking isDirty)
  useEffect(() => {
    if ((appearanceState?.configs as IAiAppearance)?.operatingHours?.isActive)
      setFormIsActive(
        (appearanceState?.configs as IAiAppearance)?.operatingHours?.isActive
      );

    if (
      (appearanceState?.configs as IAiAppearance)?.operatingHours
        ?.selectedTimezone
    )
      setSelectedFormTimezone(
        (appearanceState?.configs as IAiAppearance)?.operatingHours
          ?.selectedTimezone as string
      );

    if (
      appearanceState?.configs &&
      (appearanceState?.configs as IAiAppearance)?.operatingHours?.times
    ) {
      setFormTimes(
        adjustTimeRanges(
          (normalizedTimes(
            (appearanceState?.configs as IAiAppearance)?.operatingHours?.times
          ) as DayRangesType) || {
            sunday: [],
            monday: [],
            tuesday: [],
            wednesday: [],
            thursday: [],
            friday: [],
            saturday: []
          },
          -moment()
            .tz(
              (appearanceState?.configs as IAiAppearance)?.operatingHours
                ?.selectedTimezone ?? "UTC"
            )
            .utcOffset()
        )
      );
    }
  }, [appearanceState.showVisibilityModal]);

  return (
    <div className="lg:tw-p-6 tw-py-2 tw-bg-white tw-rounded-2xl tw-shadow-[0px_1px_2px_0px_rgba(0,0,0,0.06)] tw-flex-col  ">
      {/* Step-1 */}
      <div className="tw-px-2 tw-self-stretch tw-flex-col tw-justify-start tw-items-start tw-gap-6 tw-flex tw-mb-6">
        <div>
          <div className="tw-self-stretch tw-text-gray-900 tw-text-2xl tw-font-semibold tw-font-['Lexend'] tw-leading-loose">
            {t("VisibilityManagerEdit/key7")}
          </div>
          <div className="tw-self-stretch tw-text-gray-900 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight">
            {t("VisibilityManagerEdit/key8")}
          </div>
        </div>
        <div className="tw-font-bold tw-flex tw-items-center font-sofiaSans tw-text-sm tw-gap-3">
          <Form.Check
            disabled={
              permissions.settingsPage.widget.visibilityControl.operatingHours
                .upgrade
            }
            type="switch"
            id="custom-switch"
            checked={formIsActive}
            onChange={(e) => {
              setFormIsActive(e.target.checked);
            }}
          />

          <span className="tw-text-gray-700 tw-text-base tw-font-normal tw-font-['Lexend'] tw-leading-normal">
            {t("DashboardWidgetOperatingHours/l-01")}{" "}
            {formIsActive
              ? t("DashboardWidgetOperatingHours/l-03")
              : t("DashboardWidgetOperatingHours/l-02")}
            {permissions.settingsPage.widget.visibilityControl.operatingHours
              .upgrade && (
              <span className="tw-ml-4 tw-inline-flex">
                <ProBadge />
              </span>
            )}
          </span>
        </div>
      </div>

      <hr />

      {formIsActive && (
        <React.Fragment>
          {/* Step-2 */}
          <TimeZoneSelector
            currentTimeZone={selectedFormTimezone}
            onChange={(timezone) => handleTimeZoneChanged(timezone)}
          />

          <hr />

          {/* Step-3 */}
          <div className="tw-px-2">
            <OperatingHours
              times={formTimes}
              handleTimes={(times) => handleUpdateTimes(times)}
            />
          </div>
          <hr />
        </React.Fragment>
      )}

      {/* Action */}
      <div className="tw-px-2 tw-flex lg:tw-flex-row tw-flex-col tw-justify-between tw-gap-3 tw-px-4 tw-pb-4 tw-w-full lg:tw-items-center tw-items-end ">
        <span className="tw-text-gray-800 text-xs lg:tw-inline-flex tw-block tw-w-full">
          {t("DashboardSettingCustomizeChat/updateHint")}
        </span>

        <div className="tw-flex tw-justify-end">
          <LouiseButton
            variant="primary"
            type="button"
            className="tw-rounded-full tw-w-auto"
            onClick={handleSetVisibility}
            disabled={
              updateMcBotWidgetAppearanceResults.isLoading ||
              UpdateAiWidgetAppearanceResults.isLoading
            }
          >
            {updateMcBotWidgetAppearanceResults.isLoading ||
            UpdateAiWidgetAppearanceResults.isLoading ? (
              <Spinner size="sm" />
            ) : (
              t("VisibilityManagerEdit/key5")
            )}
          </LouiseButton>
        </div>
      </div>
    </div>
  );
};

export default VisibilityManagerEdit;
